import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty } from "lodash";
import classNames from "classnames";

import { useStateSelector } from "utils/field";
import { getOptions } from "utils/fields";
import { required, maxLength, getValidation } from "utils/fieldValidation";
import { lintelCodeLogic } from "utils/codes";
import lintelCodeTemplate from "features/Model/Enclosure/Templates/lintelCode.json";

import { actions as userActions } from "store/users";
import { actions as enclosureActions } from "features/Model/Enclosure/_ducks";
import { actions as upgradeActions } from "features/Model/Upgrades/_ducks";
import { removeFormErrors } from "store/form/actions";

import InputRow from "components/Input/Row";
import Input from "components/Input";
import Select from "components/Input/Select";
import Checkbox from "components/Input/Checkbox";
import Field from "components/Field";
import Button from "components/Button";
import Tooltip from "components/Tooltip";

import globalStyles from "components/globalStyles.module.scss";

import WarningIcon from "assets/images/icons/WarningOrange.svg";

const { addToCodeLib, deleteCode } = userActions;
const { setInitCode } = enclosureActions;
const { updateUpgradeCodes } = upgradeActions;

const charLim32 = maxLength(32);
const labelValidation = [required, charLim32];
const typeValidation = getValidation("csLintelType");
const materialValidation = getValidation("csLintelMaterial");
const insValidation = getValidation("csLintelInsulation");

const StandardLintelCode = React.memo(
    ({
        toggleOpen,
        componentId,
        fieldAccessor,
        isCodeLibrary = false,
        change,
        upgradeWhereToAddCode = {},
        submitting, // Not passed in as prop
        invalid, // Not passed in as prop
        clearInitCode,
        handleSubmit,
        removeFormErrors,
        CodeTypeSelect,
    }) => {
        const drawerAccessor = `newForm.codeDrawer.fields`;

        const setDrawerState = (value) => {
            change(`${drawerAccessor}`, value);
        };

        const codeFields = useStateSelector(`${drawerAccessor}`) || {};

        const uid = useStateSelector(`user.uid`);
        const { lintelInsType = {} } = useStateSelector(`enclosure.initCodeValues`) || {};
        const { currentPackage, selectedUpgrade } = useStateSelector(`upgradeLibrary`) || {};
        const { codes: modelCodes = {}, components = {} } = useStateSelector(`model.modelData`) || {};

        const { value: lintelTypeValue } = useStateSelector(`${drawerAccessor}.lintelCode.layers.lintelType`) || {};
        const { value: lintelMaterialValue } =
            useStateSelector(`${drawerAccessor}.lintelCode.layers.lintelMaterial`) || {};
        const { value: lintelInsValue } = useStateSelector(`${drawerAccessor}.lintelCode.layers.lintelIns`) || {};
        const lintelLabel = useStateSelector(`${drawerAccessor}.lintelCode.label`) || "";

        const lintelValue = `${lintelTypeValue}${lintelMaterialValue}${lintelInsValue}`;

        const { codeRef: editingCodeRef = null, isLibCode = false } = lintelInsType;
        const isEditing = !!editingCodeRef;

        const valueRef = useRef(null);

        useEffect(() => {
            const initialValues = {
                lintelCode: isEmpty(lintelInsType) ? lintelCodeTemplate : lintelInsType,
                addToLibrary: isLibCode,
            };

            setDrawerState(initialValues);
        }, []);

        useEffect(() => {
            valueRef.current = lintelValue;
        }, [lintelValue, lintelLabel]);

        // Prevents crashing when field values are null
        if (isEmpty(codeFields)) {
            return <React.Fragment></React.Fragment>;
        }

        return (
            <div className={globalStyles.standardCodeWrapper}>
                {/* <h3>Standard Code</h3> */}
                <InputRow gridTemplate={CodeTypeSelect ? "1fr 1fr 1fr" : "1fr 1fr"}  style={{ alignItems: "flex-end" }}>
                    {CodeTypeSelect && <CodeTypeSelect />}
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.lintelCode.label`}
                        label="Code Label"
                        placeholder="Code Label"
                        validate={labelValidation}
                        setValue={lintelLabel.toString() === valueRef.current ? lintelValue : null}
                    />
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.lintelCode.value`}
                        label="Internal Code"
                        placeholder="###########"
                        disabled={true}
                        setValue={lintelValue}
                    />
                    {!isCodeLibrary && (
                        <div data-tip="Code will automatically be updated in library" data-for="lintelCodeLibTip">
                            <Field
                                component={Checkbox}
                                name={`${drawerAccessor}.addToLibrary`}
                                label={isLibCode ? "Update code in library" : "Add to code library"}
                                large
                                disabled={isLibCode}
                                type="checkbox"
                            />
                        </div>
                    )}
                </InputRow>
                <div className={globalStyles.framingGroup}>
                    <InputRow
                        style={{
                            gridGap: "0",
                            border: "1px solid #e1eaf0",
                            borderBottom: "none",
                            borderRadius: "4px 4px 0px 0px",
                        }}
                        lastRow={true}
                    >
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.lintelCode.layers.lintelType`}
                            label="Lintel Type"
                            placeholder="Choose Lintel Type"
                            options={getOptions({ fieldKey: "csLintelType" })}
                            validate={typeValidation}
                            isFlatDesign={true}
                            borderBottom={true}
                            customBorderRadius={{ borderRadius: "4px 4px 0px 0px" }}
                        />
                    </InputRow>
                    <InputRow
                        style={{
                            gridGap: "0",
                            borderLeft: "1px solid #e1eaf0",
                            borderRight: "1px solid #e1eaf0",
                            borderTop: "none",
                            borderBottom: "none",
                        }}
                        lastRow={true}
                    >
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.lintelCode.layers.lintelMaterial`}
                            label="Material"
                            placeholder="Choose Material"
                            options={getOptions({ fieldKey: "csLintelMaterial" })}
                            validate={materialValidation}
                            isFlatDesign={true}
                            borderBottom={true}
                        />
                    </InputRow>
                    <InputRow
                        style={{
                            gridGap: "0",
                            borderBottom: "1px solid #e1eaf0",
                            borderLeft: "1px solid #e1eaf0",
                            borderRight: "1px solid #e1eaf0",
                            borderTop: "none",
                            borderRadius: "0px 0px 4px 4px",
                        }}
                        lastRow={true}
                    >
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.lintelCode.layers.lintelIns`}
                            label="Insulation"
                            placeholder="Choose Insulation"
                            options={getOptions({ fieldKey: "csLintelInsulation" })}
                            validate={insValidation}
                            isFlatDesign={true}
                            customBorderRadius={{ borderRadius: "0px 0px 4px 4px" }}
                        />
                    </InputRow>
                </div>
                <div className={globalStyles.codeDrawerWarning}>
                    <img src={WarningIcon} alt="Warning" />
                    <p>Embodied carbon calculation is not supported for Lintel codes.</p>
                </div>

                <div style={{ borderBottom: "1px solid #e1eaf0", marginTop: "1.5rem", marginBottom: "0.5rem" }}></div>

                <div className={classNames(globalStyles.buttons, globalStyles.fixedDrawerButtons)}>
                    <Button
                        large
                        type="hollow"
                        onClick={() => {
                            clearInitCode();
                            setDrawerState({});
                            toggleOpen(false);
                            removeFormErrors(`${drawerAccessor}.lintelCode.label`);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        large
                        onClick={() => {
                            handleSubmit({
                                change,
                                codeFields,
                                toggleOpen,
                                uid,
                                fieldAccessor,
                                editingCodeRef,
                                isCodeLibrary,
                                isEditing,
                                modelCodes,
                                components,
                                currentPackage,
                                selectedUpgrade,
                                upgradeWhereToAddCode,
                            });
                            // Clear wall code defaults for next time
                            clearInitCode();
                            // Reset state
                            setDrawerState({});
                        }}
                        disabled={submitting || invalid}
                    >
                        {submitting ? "Saving Code..." : "Save Code"}
                    </Button>
                </div>
                {
                    // isEditing && <p data-for="lintelDeleteCodeTip" data-tip="Deletes code from model" className={classes.delete}><Delete /> Delete Code</p>
                }
                <Tooltip id="lintelCodeLibTip" hide={!isLibCode} />
                {/* <Tooltip id="lintelDeleteCodeTip" delay /> */}
            </div>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    clearInitCode: () => dispatch(setInitCode({}, "lintelInsType")),
    handleSubmit: ({
        change,
        codeFields,
        toggleOpen,
        uid,
        fieldAccessor,
        editingCodeRef,
        isCodeLibrary,
        isEditing,
        modelCodes,
        components,
        currentPackage,
        selectedUpgrade,
        upgradeWhereToAddCode = {},
    }) => {
        const { lintelCode = {}, addToLibrary = false } = codeFields;

        toggleOpen(false);

        const {
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            ecmInputMapLocation = "parametricAnalysis",
        } = upgradeWhereToAddCode || {};

        lintelCodeLogic({
            change,
            componentCodeType: "Lintel",
            code: lintelCode,
            codeType: "S",
            isCodeLibrary,
            isEditing,
            editingCodeRef,
            dispatch,
            fieldAccessor,
            components,
            modelCodes,
            addToLibrary,
            addToCodeLib,
            uid,
            deleteCode,
            updateUpgradeCodes,
            currentPackage,
            selectedUpgrade,
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            isEcmInputMap: !isEmpty(upgradeWhereToAddCode),
            ecmInputMapLocation,
        });
    },
    removeFormErrors: (errorName) => dispatch(removeFormErrors(errorName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StandardLintelCode);
